// Section 19 and 11: Improvement proposal. 4% per season, any season, and only when a literate
// adult lives here and an improvement is left to adopt. They propose crop rotation (harvest
// +10%), a better kiln (build cost 0.8x, E2 decision: the spec's "tools cost" has no target) or
// granary lining (food in a granary spoils 1%). Choices: commit a worker with skill 3+ in the
// work for one season (their output is lost; the improvement is adopted for good) or decline.
// The adopted improvements' modifiers live in contributors/events.ts.
// Constants: config.events['improvement-proposal'].
import type { Config } from '../../config';
import {
  adultsOf,
  choiceOutcome,
  effectDelta,
  eventEntry,
  eventPrompt,
  eventRng,
  promptString,
} from '../../core/content-helpers';
import { mod } from '../../core/modifiers';
import { skillOf } from '../../core/queries';
import { defineEvent, type EventChoiceDef } from '../../core/registry';
import type { ImprovementId, Modifier, Person, ReadContext, SkillId } from '../../core/types';

const IMPROVEMENTS = ['crop-rotation', 'better-kiln', 'granary-lining'] as const;
const WORK: Record<ImprovementId, SkillId> = {
  'crop-rotation': 'farmer',
  'better-kiln': 'toolmaker',
  'granary-lining': 'builder',
};

function open(ctx: ReadContext): ImprovementId[] {
  return IMPROVEMENTS.filter((id) => !ctx.state.improvements.some((i) => i.id === id));
}

function improvementOf(s: string): ImprovementId | null {
  return (IMPROVEMENTS as readonly string[]).includes(s) ? (s as ImprovementId) : null;
}

function worker(ctx: ReadContext, id: ImprovementId, minSkill: number): Person | null {
  const skill = WORK[id];
  const able = adultsOf(
    ctx,
    (p) =>
      skillOf(p, skill) >= minSkill &&
      p.status.sick === null &&
      p.status.injured === null &&
      p.status.inCustody === null,
  ).sort((a, b) => skillOf(b, skill) - skillOf(a, skill) || (a.id < b.id ? -1 : 1));
  return able[0] ?? null;
}

export function improvementProposal(config: Config) {
  const c = config.events['improvement-proposal'];
  const plan = (ctx: ReadContext): { variant: ImprovementId; name: string } | null => {
    const literate = adultsOf(ctx, (p) => p.status.literate);
    const left = open(ctx);
    if (literate.length === 0 || left.length === 0) return null;
    const rng = eventRng(ctx, 'improvement-proposal', 'plan');
    return { variant: rng.pick(left), name: rng.pick(literate).name };
  };
  const choices: EventChoiceDef[] = [
    {
      id: 'commit',
      label: 'Commit a skilled worker for a season',
      preview: (ctx, prompt) => {
        const variant = improvementOf(promptString(prompt, 'variant'));
        if (variant === null) return [];
        const w = worker(ctx, variant, c.workerMinSkill);
        return [
          {
            key: w === null ? 'improvement-no-worker' : 'improvement-commit',
            params: { name: w?.name ?? '', job: WORK[variant], skill: c.workerMinSkill },
          },
        ];
      },
      apply: (ctx, prompt) => {
        const variant = improvementOf(promptString(prompt, 'variant'));
        const w = variant === null ? null : worker(ctx, variant, c.workerMinSkill);
        if (variant === null || w === null)
          return [choiceOutcome(prompt, 'commit', { variant: 'no-worker', name: '' })];
        return [
          effectDelta('improvement-proposal', variant, ctx.season.turn, 1, { workerOutputMul: 0 }, [
            w.id,
          ]),
          { kind: 'improvement', improvementId: variant },
          choiceOutcome(prompt, 'commit', { variant, name: w.name }),
        ];
      },
    },
    {
      id: 'decline',
      label: 'Decline',
      preview: null,
      apply: (_ctx, prompt) => [choiceOutcome(prompt, 'decline')],
    },
  ];
  return defineEvent({
    id: 'improvement-proposal',
    label: 'Improvement proposal',
    section: 'S19',
    baseWeight: c.baseChance,
    seasons: ['spring', 'summer', 'autumn', 'winter'],
    foreshadow: null,
    ledgerTag: 'improvement',
    weight: (ctx) => (plan(ctx) === null ? 0 : c.baseChance),
    text: (ctx) => {
      const p = plan(ctx);
      return eventEntry('improvement-proposal', {
        variant: p?.variant ?? 'crop-rotation',
        name: p?.name ?? '',
      });
    },
    apply: (ctx) => {
      const p = plan(ctx);
      if (p === null) return [];
      return [
        eventPrompt(ctx, 'improvement-proposal', choices, {
          params: { variant: p.variant, name: p.name },
          defaultChoiceId: 'decline',
        }),
      ];
    },
    choices,
    modifierTargets: ['output'],
    modifiers(_ctx, target, effect): Modifier[] {
      const mul = effect.params.workerOutputMul;
      if (target.kind !== 'output' || mul === undefined) return [];
      return effect.subjectIds?.includes(target.personId) === true
        ? [mod.mul('event:improvement-proposal', mul, 'working on an improvement')]
        : [];
    },
  });
}
